/**
 * CodingLayout — the app shell for the Teammate Code surface.
 *
 * A sibling of `AppLayout` with one substitution: `CodingSidebar` stands in for
 * `AppSidebar`. Coding is app-scoped like every other surface, but its rail is
 * its own — sessions, not the app nav — and running both meant two rails on
 * screen at once.
 *
 * Everything else is deliberately identical to `AppLayout`: the same
 * `AppProvider`, the same 280px `SidebarProvider`, the same visual-viewport
 * sizing, and the same settings dialogs, because the footer nav that opens them
 * is the platform's own and has to keep working here.
 *
 * @module components/layout/coding-layout
 */

import type { ReactNode } from "react"
import { useParams } from "react-router-dom"

import { CodingSidebar } from "@/components/coding/coding-sidebar"
import {
  GithubGateScreen,
  githubGateState,
  useGithubConnection,
} from "@/components/coding/github-connect"
import { AppSettingsDialog } from "@/components/apps/app-settings-dialog"
import { WorkspaceSettingsDialog } from "@/components/settings/workspace-settings-dialog"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import { AppProvider } from "@/contexts/app-context"
import { useVisualViewport } from "@/hooks/use-visual-viewport"
import { AppMobileTopBar } from "./app-mobile-top-bar"

interface CodingLayoutProps {
  children: ReactNode
}

export function CodingLayout({ children }: CodingLayoutProps) {
  useVisualViewport()
  const { appId } = useParams<{ appId: string }>()
  const connection = useGithubConnection()
  const gate = githubGateState(connection)

  /*
    Until a repository is reachable there is no coding surface to lay out: the
    session rail would list sessions nobody can start, and the composer would
    take instructions it cannot act on. So the gate replaces the whole shell
    rather than sitting inside it.

    It keeps its own way back to the main app, because that link normally lives
    in the rail this is standing in for.
  */
  if (gate !== "ready") {
    return (
      <AppProvider>
        <div
          className="app-vv-shell h-[var(--app-vh,100dvh)] overflow-y-auto bg-white dark:bg-background"
          style={{ marginTop: "var(--app-vv-offset-top, 0px)" }}
        >
          <GithubGateScreen
            state={gate}
            appId={appId}
            onRetry={() => void connection.refetch()}
            retrying={connection.isFetching}
          />
        </div>
      </AppProvider>
    )
  }

  return (
    <AppProvider>
      <SidebarProvider style={{ "--sidebar-width": "280px" } as React.CSSProperties}>
        <CodingSidebar />
        {/*
          Sized to the VISUAL viewport (`--app-vh`) and offset by
          `--app-vv-offset-top` for the same reason as AppLayout: iOS Safari
          does not shrink `dvh` for the soft keyboard, which would leave the
          coding composer behind it. Both custom properties are inert on
          desktop.
        */}
        <SidebarInset
          className="app-vv-shell h-[var(--app-vh,100dvh)] overflow-y-auto bg-white dark:bg-background"
          style={{ marginTop: "var(--app-vv-offset-top, 0px)" }}
        >
          <AppMobileTopBar />
          {children}
        </SidebarInset>
      </SidebarProvider>
      {/* Single mount, opened from anywhere via useSettingsDialogsStore — the
          footer nav's Settings entry is one of the callers. */}
      <WorkspaceSettingsDialog />
      <AppSettingsDialog />
    </AppProvider>
  )
}

CodingLayout.displayName = "CodingLayout"
